import _invert from 'lodash/invert';

export const countryMap: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  JM: 'Jamaica',
  VC: 'Saint Vincent and the Grenadines',
};

export const countryMapToShortened: Record<string, string> = _invert(countryMap);

export const jamaicanParishesMap: Record<string, string> = {
  '01': 'Kingston Parish',
  '02': 'St. Andrew Parish',
  '03': 'St. Thomas Parish',
  '04': 'Portland Parish',
  '05': 'St. Mary Parish',
  '06': 'St. Ann Parish',
  '07': 'Trelawny Parish',
  '08': 'St. James Parish',
  '09': 'Hanover Parish',
  10: 'Westmoreland Parish',
  11: 'St. Elizabeth Parish',
  12: 'Manchester Parish',
  13: 'Clarendon',
  14: 'St. Catherine Parish',
};

export const jamaicanParishesMapToShortened: Record<string, string> = _invert(jamaicanParishesMap);

export const saintVincentAndTheGrenadinesParishesMap: Record<string, string> = {
  '01': 'Charlotte',
  '02': 'St. Andrew',
  '03': 'St. David',
  '04': 'St. George',
  '05': 'St. Patrick',
  '06': 'Grenadines',
};

export const saintVincentAndTheGrenadinesParishesMapToShortened: Record<string, string> = _invert(
  saintVincentAndTheGrenadinesParishesMap
);

export const canadianProvincesMap: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Québec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

export const puertoRicoMapToShortened: Record<string, string> = {
  Humacao: 'PR',
  Naguabo: 'PR',
  'San Juan': 'PR',
};

export const canadianProvincesMapToShortened: Record<string, string> = { ..._invert(canadianProvincesMap), Quebec: 'QC' };

export const USStatesMap: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AR: 'Arkansas',
  AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
};

export const USStatesMapToShortened: Record<string, string> = { ..._invert(USStatesMap), DC: 'DC' };

// Partial, not Record<string, Record<string, string>>: only 4 country codes
// are actually populated - an unrecognized code (e.g. a bad `countryCode`
// URL param) makes this lookup undefined at runtime. See #21057 for why a
// plain Record type here is dangerous (it hides that from both the compiler
// and no-unnecessary-condition, inviting someone to delete a real `?.` guard).
export const countryToStateMap: Partial<Record<string, Record<string, string>>> = {
  US: USStatesMap,
  CA: canadianProvincesMap,
  JM: jamaicanParishesMap,
  VC: saintVincentAndTheGrenadinesParishesMap,
};

// Same reasoning as countryToStateMap above - only 5 country names populated.
export const countryToShortenedStateMap: Partial<Record<string, Record<string, string>>> = {
  'United States': USStatesMapToShortened,
  'Puerto Rico': puertoRicoMapToShortened,
  Canada: canadianProvincesMapToShortened,
  Jamaica: jamaicanParishesMapToShortened,
  'Saint Vincent and the Grenadines': saintVincentAndTheGrenadinesParishesMapToShortened,
};

/**
 * Cities for which we can't create a city page
 *
 * {
 *  stateCode: ['City Name', 'City Name']
 * }
 *
 * stateCode should be the ISO code for the state in which the city is located
 * 'City Name' should match what's in the DB exactly (spelling/capitalization)
 *
 * Please leave a reason for why it's unsupported in a comment beside the entry
 */
// Partial, not Record<string, string[]>: only a handful of state codes have
// an entry (currently just PR) - most lookups are misses. A plain Record type
// here previously caused a real bug (see #21057) where the compiler treated
// `unsupportedCities[code]` as always-defined, ESLint flagged the resulting
// `?.` as "unnecessary", and removing it turned a safe lookup into a runtime
// crash for every non-PR state.
export const unsupportedCities: Partial<Record<string, string[]>> = {
  PR: ['Humacao'], // Reason: can't find a way to get Mapbox to return the actual city
};
